/**
 * useStoryline — fetch one storyline's full detail (GetStoryline) for the Storyline
 * detail page. Owns storyline / loading / error + a manual reload key so the header
 * and Description card read ONE shared detail and refresh together after an edit
 * (mirror of useAssetDetail). GetStoryline carries linkedAssets so the page renders
 * without a follow-up ListAssets.
 */
import { useCallback, useEffect, useState } from 'react';
import { storylineClient } from '../../../client/storylineClient';
import type { Storyline } from '../../../client/IvtHubClient';

export interface UseStoryline {
  storyline: Storyline | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useStoryline(storylineId: string | null): UseStoryline {
  const [storyline, setStoryline] = useState<Storyline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (!storylineId) {
      setStoryline(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const s = await storylineClient.get(storylineId);
        if (!cancelled) setStoryline(s ?? null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storylineId, reloadKey]);

  return { storyline, loading, error, reload };
}
